import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  LicensePaymentStatus,
  type PartyLicenseDto,
} from "Infrastructure/Api/Api";
import type { RootStateType } from "State/Store";
import { useSelector } from "react-redux";

export type HunterLicenseGateState =
  | "loading"
  | "notApplicable"
  | "active"
  | "paymentPending"
  | "noLicense";

type Options = {
  staleTime?: number;
  refetchInterval?: number;
};

const isPendingPayment = ({ paymentStatus }: PartyLicenseDto) =>
  paymentStatus === LicensePaymentStatus.Initiated ||
  paymentStatus === LicensePaymentStatus.Processing;

const getGateState = (
  isHunter: boolean,
  isLoading: boolean,
  isError: boolean,
  licenses: Array<PartyLicenseDto> | undefined,
): HunterLicenseGateState => {
  if (!isHunter) {
    return "notApplicable";
  }

  if (isLoading) {
    return "loading";
  }

  // Fail-open: never lock users out on a transient API failure.
  if (isError || !licenses) {
    return "active";
  }

  if (
    licenses.some(
      ({ paymentStatus }) => paymentStatus === LicensePaymentStatus.Success,
    )
  ) {
    return "active";
  }

  if (licenses.some(isPendingPayment)) {
    return "paymentPending";
  }

  return "noLicense";
};

export const useHunterLicenseGate = (options?: Options) => {
  const isHunter = useIsCurrentUserHunter();
  const currentUser = useLoggedInUser();
  // The persisted (redux-persist) user is stale right after a context-switch
  // reload — it can still be the hunter while the JWT already belongs to the
  // new context, and fetching the hunter's licenses with that token 403s.
  // Hold the query until the fresh user-info lands; a disabled query keeps
  // serving cached data, so mid-session refreshes don't flap the gate.
  const isUserFetching = useSelector(
    (state: RootStateType) => state.auth.isUserFetching,
  );
  const { data, isLoading, isError } = usePartyLicensesQuery(
    isHunter && !isUserFetching ? currentUser?.partyPublicID : undefined,
    options,
  );

  const state = getGateState(isHunter, isLoading, isError, data?.items);
  const pendingLicenses = data?.items.filter(isPendingPayment) ?? [];

  return { state, pendingLicenses };
};
